import React, {useEffect, useState} from 'react';
import LogoutButton from '../Auth/LogoutButton';
import {UPLOAD_URL_V2, BASE_URL} from "../../config/config";
import {postWithAuth, patchWithAuth} from "../Network/HttpMethods";
import {
    ActiveTagsContainer,
    ContentContainer,
    FilterButton,
    FilterInputContainer,
    FilterLabel,
    FilterSection,
    Heading,
    MediaList,
    PageContainer,
    Sidebar,
    SidebarItem,
    TagChip,
    TagInput,
    UploadSection
} from "./StyledComponents";
import {GreenButton} from "../Shared/RedButton";
import {fetchFileLinkMap, fetchThumbnailsByFileIds, fetchUploadedFilesPaginated} from "../Network/ServiceMethods";
import {Thumbnail} from "./Thumbnail";
import SmartUploadInput from "../Files/SmartUploadInput";
import CopyableLink from "../Shared/CopyableLink";
import {LinkControls, MediaCard, MediaDetails, MediaThumbnail} from "./StyledComponents";
import Pagination from "../Shared/Pagination";
import HashtagText from "../Shared/HashtagText";
import {useNavigate} from "react-router-dom";

function Dashboard() {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [generatedLinks, setGeneratedLinks] = useState([]);
    const [linkListingStatus, setLinkListingStatus] = useState({});
    const [thumbnailMap, setThumbnailMap] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [activeTags, setActiveTags] = useState([]);
    const navigate = useNavigate();

    const loadFiles = async (page, size, tags = null) => {
        setIsLoading(true);
        try {
            const pageData = await fetchUploadedFilesPaginated(page, size, tags);
            setUploadedFiles(pageData.content);
            setTotalPages(pageData.totalPages);
            setTotalElements(pageData.totalElements);
            setCurrentPage(pageData.number);
        } catch (err) {
            console.error("Failed to load files:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadFiles(currentPage, pageSize, activeTags.length > 0 ? activeTags : null);
    }, [currentPage, pageSize, activeTags]);

    useEffect(() => {
        const loadThumbnails = async () => {
            const fileIds = uploadedFiles.map(f => f.id);
            if (fileIds.length > 0) {
                try {
                    const map = await fetchThumbnailsByFileIds(fileIds);
                    setThumbnailMap(map);

                } catch (err) {
                    console.error("Failed to load thumbnails:", err);
                }
            }
        };

        loadThumbnails();
    }, [uploadedFiles]);

    useEffect(() => {
        const loadLinkData = async () => {
            const fileIds = uploadedFiles.map((file) => file.id);
            if (fileIds.length > 0) {
                try {
                    const linkMetaMap = await fetchFileLinkMap(fileIds);
                    // linkMetaMap is a direct map of fileExternalId -> FileLinkMetaVM
                    const linkMap = {};
                    const statusMap = {};

                    if (linkMetaMap) {
                        Object.entries(linkMetaMap).forEach(([fileId, linkMeta]) => {
                            linkMap[fileId] = linkMeta.id;
                            statusMap[fileId] = linkMeta.isListed || false;
                        });
                    }

                    setGeneratedLinks(linkMap);
                    setLinkListingStatus(statusMap);
                } catch (error) {
                    console.error("Failed to load link data:", error);
                }
            }
        };

        loadLinkData();
    }, [uploadedFiles]);

    const generateLink = async (fileId) => {
        try {
            const response = await postWithAuth(`${BASE_URL}/links/${fileId}`, {});
            const data = await response.json();
            setGeneratedLinks((prevLinks) => ({...prevLinks, [fileId]: data.id}));
            setLinkListingStatus((prevStatus) => ({...prevStatus, [fileId]: data.isListed || false}));
        } catch (error) {
            console.error("Error generating file link:", error);
        }
    };

    const toggleLinkListing = async (fileId) => {
        try {
            let linkId = generatedLinks[fileId];

            // If no link exists, create one first
            if (!linkId) {
                const createResponse = await postWithAuth(`${BASE_URL}/links/${fileId}`, {});
                const createData = await createResponse.json();
                linkId = createData.id;
                setGeneratedLinks((prevLinks) => ({...prevLinks, [fileId]: linkId}));
                setLinkListingStatus((prevStatus) => ({...prevStatus, [fileId]: createData.isListed || false}));
            }

            // Now toggle the listing status
            const response = await patchWithAuth(`${BASE_URL}/links/${linkId}/toggle_listing`, null);
            const data = await response.json();
            setLinkListingStatus((prevStatus) => ({...prevStatus, [fileId]: data.isListed}));
        } catch (error) {
            console.error("Error toggling link listing:", error);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(0);
    };

    const handleFileUpload = () => {
        loadFiles(currentPage, pageSize, activeTags.length > 0 ? activeTags : null);
    };

    const handleTagSearch = () => {
        const tags = tagInput
            .trim()
            .split(/\s+/)
            .filter(tag => tag.length > 0);
        setActiveTags(tags);
        setCurrentPage(0);
    };

    const handleClearTags = () => {
        setTagInput('');
        setActiveTags([]);
        setCurrentPage(0);
    };

    const handleHashtagClick = (tag) => {
        // Add the tag to the active tags if not already present
        const newTags = activeTags.includes(tag) ? activeTags : [...activeTags, tag];
        setActiveTags(newTags);
        setTagInput(newTags.join(' '));
        setCurrentPage(0);
    };

    return (
        <PageContainer>
            {/* Sidebar Navigation */}
            <Sidebar>
                <SidebarItem onClick={() => navigate('/dashboard')}>Dashboard</SidebarItem>
                <SidebarItem onClick={() => navigate('/public-videos')}>Public Videos</SidebarItem>
                <LogoutButton/>
            </Sidebar>

            {/* Main Content */}
            <ContentContainer>
                <Heading>Welcome to Your Dashboard</Heading>
                <UploadSection>
                    <SmartUploadInput
                        uploadUrl={UPLOAD_URL_V2}
                        onUploadComplete={handleFileUpload}/>
                </UploadSection>

                {/* Filter by Tags */}
                <FilterSection>
                    <FilterLabel>Filter by Tags (space-separated)</FilterLabel>
                    <FilterInputContainer>
                        <TagInput
                            type="text"
                            placeholder="Enter tags separated by spaces (e.g., vacation photos 2024)"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyUp={(e) => {
                                if (e.key === 'Enter') {
                                    handleTagSearch();
                                }
                            }}
                        />
                        <FilterButton onClick={handleTagSearch}>
                            Search
                        </FilterButton>
                        {activeTags.length > 0 && (
                            <FilterButton variant="secondary" onClick={handleClearTags}>
                                Clear
                            </FilterButton>
                        )}
                    </FilterInputContainer>
                    {activeTags.length > 0 && (
                        <ActiveTagsContainer>
                            {activeTags.map((tag, index) => (
                                <TagChip key={index}>{tag}</TagChip>
                            ))}
                        </ActiveTagsContainer>
                    )}
                </FilterSection>

                {/* Uploaded files list */}
                {isLoading ? (
                    <div>Loading files...</div>
                ) : uploadedFiles.length > 0 ? (
                    <>
                        <MediaList>
                            {uploadedFiles.map((file, index) => (
                                <MediaCard key={index}>
                                    <MediaThumbnail>
                                        <Thumbnail url={thumbnailMap[file.id]} />
                                    </MediaThumbnail>

                                    <MediaDetails>
                                        <div>
                                            <HashtagText
                                                text={file.description || "Unnamed File"}
                                                onHashtagClick={handleHashtagClick}
                                            /> &nbsp;
                                            ({Math.round(file.size / 1024)} KB)
                                        </div>

                                        <LinkControls>
                                            <GreenButton
                                                disabled={!!generatedLinks[file.id]}
                                                onClick={() => generateLink(file.id)}
                                            >
                                                Link File
                                            </GreenButton>
                                            {generatedLinks[file.id] && (
                                                <CopyableLink linkId={generatedLinks[file.id]} />
                                            )}
                                            <GreenButton
                                                onClick={() => toggleLinkListing(file.id)}
                                            >
                                                {linkListingStatus[file.id] ? '🔓 Unlist' : '🔒 List Publicly'}
                                            </GreenButton>
                                        </LinkControls>
                                    </MediaDetails>
                                </MediaCard>
                            ))}
                        </MediaList>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </>
                ) : (
                    <div>No files uploaded yet.</div>
                )}
            </ContentContainer>
        </PageContainer>
    );
}

export default Dashboard;

